/**
 * Configurações → «CRM do Odoo»: equipas de onde vêm as leads do simulador
 * (Prospeção + Vendas), equipa para onde vai a oportunidade ganha e etapa
 * da proposta enviada. «Automático» = pelo nome no Odoo (Prospeção, Vendas,
 * Propostas Entregues).
 */
import { useEffect, useState } from "react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { getOdooCrmOptions, type OdooCrmConfig, type OdooCrmOptions } from "@/lib/api"

const AUTO = "__auto__"

export function OdooCrmCard({
  shell, value, onChange,
}: {
  shell: AppShell | null
  value: OdooCrmConfig
  onChange: (next: OdooCrmConfig) => void
}) {
  const [options, setOptions] = useState<OdooCrmOptions | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!shell) return
    let cancelled = false
    getOdooCrmOptions(shell)
      .then((o) => { if (!cancelled) setOptions(o) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Falha a ler o Odoo") })
    return () => { cancelled = true }
  }, [shell])

  const teamName = (id: number | null | undefined) => options?.teams.find((t) => t.id === id)?.name
  const stageName = (id: number | null | undefined) => options?.stages.find((s) => s.id === id)?.name

  function field(
    key: keyof OdooCrmConfig,
    label: string,
    help: string,
    items: Array<{ id: number; name: string }>,
    autoName: string | undefined,
  ) {
    const current = value[key]
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <Select
          value={current ? String(current) : AUTO}
          onValueChange={(v) => {
            const next = { ...value }
            if (v === AUTO) delete next[key]
            else next[key] = Number(v)
            onChange(next)
          }}
          disabled={!options}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTO}>Automático{autoName ? ` (${autoName})` : " (não encontrada)"}</SelectItem>
            {items.map((i) => (
              <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
    )
  }

  // «Automático» mostra o que o nome encontra — sem o override das Configurações.
  const auto = options?.resolved
  return (
    <Card>
      <CardHeader>
        <CardTitle>CRM do Odoo</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          A pesquisa de leads dos assistentes procura nas equipas de prospeção e de vendas do Odoo.
          Ao enviar a proposta por email, a lead passa à etapa escolhida; no «Ganho», a oportunidade
          passa à equipa de vendas, com o comercial, o cliente e o valor s/ IVA, e fica ganha.
          «Perdido» não mexe no Odoo.
        </p>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {field("prospect_team_id", "Equipa de prospeção", "Leads criadas pelos comerciais/SDR.",
            options?.teams ?? [], value.prospect_team_id ? undefined : teamName(auto?.prospectTeamId))}
          {field("sales_team_id", "Equipa de vendas", "Também pesquisada; recebe as oportunidades ganhas.",
            options?.teams ?? [], value.sales_team_id ? undefined : teamName(auto?.salesTeamId))}
          {field("proposal_stage_id", "Etapa da proposta enviada", "Para onde vai a lead quando a proposta segue por email.",
            options?.stages.filter((s) => !s.is_won) ?? [], value.proposal_stage_id ? undefined : stageName(auto?.proposalStageId))}
        </div>
      </CardContent>
    </Card>
  )
}
